import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Dialog } from 'src/components/Dialog';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { updateUser } from 'src/models/user/user';
import { defaultStats } from 'src/models/user/utils';
import { FormAddableSelect } from 'src/shared/Forms/Selects/AddableSelect';
import toast from 'src/utils/toast';

interface AddMeasurementDialogProps {
  open: boolean;
  onClose: () => void;
}

export const AddMeasurementDialog = ({
  open,
  onClose,
}: AddMeasurementDialogProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'assessmentQuestionField',
  });
  const { t: tGlobal } = useTranslation();
  const { session, sessionTrainer, user: currentUser } = useSession();
  const queryClient = useQueryClient();

  // Get default metrics labels - matches Ember's defaultMetrics computed property
  const defaultMetrics = defaultStats
    .filter((stat) => stat.stat !== 'height')
    .map((stat) => stat.label);

  // Build statOptions: default metrics + sessionTrainer's healthInfoMetrics
  // Matches Ember's statOptions computed property (only uses currentWhiteLabeler, not currentUser)
  const statOptions = (() => {
    const options = [...defaultMetrics];
    if (sessionTrainer?.healthInfoMetrics) {
      options.push(...sessionTrainer.healthInfoMetrics);
    }
    // Remove duplicates
    return [...new Set(options)].map((label) => ({ id: label, title: label }));
  })();

  // Get initial selected tags: default metrics + healthInfoMetrics - defaultHealthMetrics
  // Matches Ember's defaultStatOptions computed property
  // Uses sessionTrainer (currentWhiteLabeler) first, fallback to currentUser
  const getInitialSelectedTags = (): string[] => {
    const user = sessionTrainer || currentUser;
    if (!user) return [];

    let stats = [...defaultMetrics];
    if (user.healthInfoMetrics) {
      stats = stats.concat(user.healthInfoMetrics);
    }
    if (user.defaultHealthMetrics && user.defaultHealthMetrics.length > 0) {
      stats = stats.filter((r) => !user.defaultHealthMetrics?.includes(r));
    }
    return stats;
  };

  // Update health fields - matches Ember's updateHealthFields action
  // This calculates defaultHealthMetrics and healthInfoMetrics from selected fields
  const calculateHealthFields = (fields: string[]) => {
    // defaultFields = default metrics that are NOT selected (these go to defaultHealthMetrics)
    const defaultFields = defaultMetrics.filter((f) => !fields.includes(f));

    // custom = selected fields that are NOT default metrics (these are custom metrics)
    // In Ember, these are converted to EmberObject with stat, label, value
    // In React, we just store the label strings in healthInfoMetrics
    const custom = fields.filter((f) => !defaultMetrics.includes(f));

    return {
      defaultHealthMetrics: defaultFields,
      healthInfoMetrics: custom,
    };
  };

  const updateUserMutation = useMutation({
    mutationFn: (data: {
      defaultHealthMetrics: string[];
      healthInfoMetrics: string[];
    }) => {
      const user = sessionTrainer || currentUser;
      if (!user) {
        throw new Error('User not available');
      }
      return updateUser(user.id, data);
    },
    onSuccess: () => {
      toast.success(
        `${tGlobal('trainer.client.one')} ${tGlobal('measurement.plural')} ${t(
          'saved'
        )}!`
      );
      queryClient.invalidateQueries({
        queryKey: ['user', session?.trainerId],
      });
      queryClient.invalidateQueries({
        queryKey: ['currentTrainer'],
      });
      queryClient.invalidateQueries({
        queryKey: ['currentUser'],
      });
      // Close without rollback (like Ember's closeAndResetModal(true))
      onClose();
    },
    onError: () => {
      toast.error(t('errorUpdatingMeasurements'));
    },
  });

  interface FormValues {
    selectedTags: string[];
  }

  const handleSubmit = (values: FormValues) => {
    const user = sessionTrainer || currentUser;
    if (!user) return;

    const { defaultHealthMetrics, healthInfoMetrics } =
      calculateHealthFields(values.selectedTags);

    // Check if values changed
    const currentDefaultMetrics = user.defaultHealthMetrics?.toString() || '';
    const newDefaultMetrics = defaultHealthMetrics.toString();
    const currentHealthInfoMetrics = user.healthInfoMetrics?.toString() || '';
    const newHealthInfoMetricsStr = healthInfoMetrics.toString();

    if (
      currentDefaultMetrics !== newDefaultMetrics ||
      currentHealthInfoMetrics !== newHealthInfoMetricsStr
    ) {
      // Save the changes (like Ember's save action)
      updateUserMutation.mutate({
        defaultHealthMetrics,
        healthInfoMetrics,
      });
    } else {
      // No changes, just close
      onClose();
    }
  };

  const handleClose = () => {
    // Formik will handle reset on close via initialValues
    onClose();
  };

  // Don't render if no user available
  if (!sessionTrainer && !currentUser) {
    return null;
  }

  const initialValues: FormValues = {
    selectedTags: getInitialSelectedTags(),
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ submitForm, setFieldValue }) => (
        <Dialog
          open={open}
          onClose={handleClose}
          title={`${t('add')}/${t('remove')} ${tGlobal('measurement.plural')}`}
          maxWidth="sm"
          fullWidth
          actions={[
            {
              label: t('save'),
              onClick: submitForm,
              variant: 'contained',
              loading: updateUserMutation.isPending,
            },
          ]}
        >
          <Form>
            <Box sx={{ p: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {t('addOrRemoveCustom')} {tGlobal('trainer.client.one')}{' '}
                {tGlobal('measurement.plural')}
              </Typography>
              <FormAddableSelect
                name="selectedTags"
                label=""
                options={statOptions}
                placeholder={`${t('add')} ${tGlobal('measurement.one')}`}
                keepUnmatched
                onChange={(fields) => {
                  // Update health fields when tags change (like Ember's tag-autocomplete with action)
                  // This is called immediately but doesn't save yet
                  const { defaultHealthMetrics, healthInfoMetrics } =
                    calculateHealthFields(fields);
                  // Store in formik values for later use in submit
                  setFieldValue('defaultHealthMetrics', defaultHealthMetrics);
                  setFieldValue('healthInfoMetrics', healthInfoMetrics);
                }}
              />
            </Box>
          </Form>
        </Dialog>
      )}
    </Formik>
  );
};
